import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertBar from '../components/AlertBar';
import axios from 'axios';
import * as paymentService from '../services/paymentService';

const Donate = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Amount & Frequency
    amount: '50',
    frequency: 'one-time',
    purpose: 'general',
    processor: 'stripe',
    
    // Step 2: Donation Intent (optional)
    reason: '',
    
    // Step 3: Legal
    agreeToTerms: false,
    agreeToProcessing: false
  });

  const [errors, setErrors] = useState({});
  const [cardFees, setCardFees] = useState(() => {
    // Calculate initial fees based on initial amount
    const initialAmount = 50;
    return paymentService.calculateFees(initialAmount, 'stripe');
  });

  const calculateFees = useCallback((amount = null, processor = null) => {
    const amt = amount !== null ? amount : (parseFloat(formData.amount) || 50);
    const proc = processor !== null ? processor : formData.processor;
    const fees = paymentService.calculateFees(amt, proc);
    setCardFees(fees);
  }, [formData.amount, formData.processor]);

  useEffect(() => {
    if (window.initPathioScripts) {
      window.initPathioScripts();
    }
    // Calculate fees whenever amount or processor changes
    calculateFees();
  }, [calculateFees]);

  // Prefill from donation-details campaign page
  useEffect(() => {
    try {
      const p = sessionStorage.getItem('prefillDonation');
      if (p) {
        const obj = JSON.parse(p);
        setFormData(prev => ({
          ...prev,
          amount: obj.amount ? obj.amount.toString() : prev.amount,
          frequency: obj.frequency || prev.frequency,
          donorName: obj.donorName || prev.donorName,
          donorEmail: obj.donorEmail || prev.donorEmail,
          purpose: obj.purpose || prev.purpose
        }));
        setTimeout(() => calculateFees(), 0);
        sessionStorage.removeItem('prefillDonation');
      }
    } catch (err) {
      console.warn('Failed to apply prefillDonation', err);
    }
  }, [calculateFees]);

  const presetAmounts = [5, 10, 25, 50, 100, 250, 500];
  
  const donationFrequencies = [
    { value: 'one-time', label: 'One-Time Donation' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' }
  ];

  const donationPurposes = [
    { value: 'general', label: 'General Fund' },
    { value: 'programs', label: 'Programs & Outreach' },
    { value: 'operations', label: 'Operations & Platform' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Recalculate fees if amount changes
    if (name === 'amount') {
      const newAmount = parseFloat(value) || 0;
      calculateFees(newAmount, formData.processor);
    } else if (name === 'processor') {
      const currentAmount = parseFloat(formData.amount) || 50;
      calculateFees(currentAmount, value);
    }
  };

  const handlePresetAmount = (amount) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString()
    }));
    // Calculate fees immediately with the new amount
    calculateFees(amount, formData.processor);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        newErrors.amount = 'Please enter a valid amount';
      }
    } else if (step === 2) {
      // Step 2 is just informational - Stripe will handle payment
      // No validation needed
    } else if (step === 3) {
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to terms and conditions';
      }
      if (!formData.agreeToProcessing) {
        newErrors.agreeToProcessing = 'You must agree to payment processing';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setAlert({
        type: 'danger',
        message: 'Please fix the errors above before continuing.'
      });
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      setAlert({
        type: 'danger',
        message: 'Please agree to all terms before submitting.'
      });
      setShowAlert(true);
      return;
    }

    setIsProcessing(true);
    setAlert({
      type: 'info',
      message: 'Redirecting to secure checkout...'
    });
    setShowAlert(true);

    try {
      const response = await axios.post('/api/create-checkout-session', {
        amount: parseFloat(formData.amount),
        fees: cardFees.fees,
        frequency: formData.frequency,
        purpose: formData.purpose,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Donation error:', error);
      setAlert({
        type: 'danger',
        message: '❌ An error occurred while setting up checkout. Please try again.'
      });
      setShowAlert(true);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />

      <main>
        <style>{`
          @media screen and (max-width: 576px) {
            .donate-step-container {
              padding: clamp(20px, 5vw, 30px) !important;
            }
            .donate-form-outer {
              padding: clamp(15px, 4vw, 25px) !important;
            }
            .donate-category-grid {
              grid-template-columns: 1fr !important;
              gap: clamp(15px, 4vw, 20px) !important;
            }
            .donate-amount-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: clamp(8px, 2vw, 12px) !important;
            }
            .donate-amount-grid button {
              padding: clamp(12px, 3vw, 16px) !important;
              font-size: clamp(14px, 3.5vw, 16px) !important;
            }
            .donate-summary-grid {
              grid-template-columns: 1fr 1fr !important;
              gap: clamp(12px, 3vw, 16px) !important;
            }
            .donate-nav-buttons {
              flex-direction: column !important;
              gap: clamp(10px, 3vw, 14px) !important;
            }
            .donate-nav-buttons button {
              width: 100% !important;
              margin-left: 0 !important;
              text-align: center !important;
            }
          }
          @media screen and (max-width: 400px) {
            .donate-amount-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
        `}</style>

        {/* Alert Bar */}
        {showAlert && (
          <AlertBar
            type={alert.type}
            message={alert.message}
            onClose={() => setShowAlert(false)}
          />
        )}

        {/* Breadcrumb */}
        <section className="ul-breadcrumb ul-section-spacing">
          <div className="ul-container">
            <h2 className="ul-breadcrumb-title">Make a Donation</h2>
            <ul className="ul-breadcrumb-nav">
              <li><a href="/">Home</a></li>
              <li><span className="separator"><i className="flaticon-right"></i></span></li>
              <li>Donate</li>
            </ul>
          </div>
        </section>

        {/* Donation Form */}
        <section className="ul-section-spacing">
          <div className="ul-container">
            <div style={{marginBottom: 'clamp(50px, 5.26vw, 70px)', textAlign: 'center'}}>
              <h2 className="ul-section-title" style={{marginBottom: 'clamp(20px, 2.1vw, 30px)', fontSize: 'clamp(32px, 3.15vw, 48px)', fontWeight: 700, color: 'var(--ul-black)'}}>
                Support Pathio
              </h2>
              <p className="ul-section-descr" style={{maxWidth: '800px', margin: '0 auto', fontSize: 'clamp(16px, 1.26vw, 20px)', lineHeight: 1.8, color: 'rgba(0, 0, 0, 0.75)'}}>
                Pathio is a student-led 501(c)(3) nonprofit built to make healthcare career pathways less confusing and more accessible—especially for students from Uzbek and broader Central Asian communities.
              </p>
            </div>

            <div className="row gx-5 gy-4">
              {/* Donation Form - Left Column */}
              <div className="col-lg-8">
                <div className="ul-inner-contact-container donate-form-outer" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 50px)', borderRadius: 'clamp(15px, 1.05vw, 20px)', boxShadow: '0 5px 30px rgba(0, 0, 0, 0.08)'}}>
                  <form onSubmit={handleSubmit}>
                  {/* Progress Indicator */}
                  <div style={{margin: '0 0 clamp(40px, 4.21vw, 60px) 0', position: 'relative'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', padding: '0 clamp(20px, 2.1vw, 30px)'}}>
                      {/* Progress Line */}
                      <div style={{
                        position: 'absolute',
                        top: 'clamp(20px, 2.1vw, 24px)',
                        left: 'clamp(20px, 2.1vw, 30px)',
                        right: 'clamp(20px, 2.1vw, 30px)',
                        height: '2px',
                        background: 'rgba(0, 0, 0, 0.08)',
                        zIndex: 0
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${((activeStep - 1) / 2) * 100}%`,
                          background: 'var(--ul-primary)',
                          transition: 'width 0.4s ease',
                          borderRadius: '2px'
                        }}></div>
                      </div>

                      {[1, 2, 3].map((step) => (
                        <div key={step} style={{textAlign: 'center', position: 'relative', zIndex: 1, flex: 1}}>
                  <div
                    style={{
                              width: 'clamp(40px, 2.63vw, 48px)',
                              height: 'clamp(40px, 2.63vw, 48px)',
                              margin: '0 auto clamp(10px, 1.05vw, 14px)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                              fontWeight: 600,
                              color: activeStep >= step ? 'var(--white)' : 'rgba(0, 0, 0, 0.3)',
                              backgroundColor: activeStep >= step ? 'var(--ul-primary)' : 'var(--white)',
                              border: activeStep >= step ? 'none' : '2px solid rgba(0, 0, 0, 0.1)',
                              cursor: step < activeStep ? 'pointer' : 'default',
                              transition: 'all 0.3s ease',
                              fontSize: 'clamp(15px, 1.05vw, 17px)',
                              boxShadow: activeStep >= step ? '0 4px 12px rgba(20, 180, 180, 0.25)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => step < activeStep && setActiveStep(step)}
                            onMouseEnter={(e) => {
                              if (step < activeStep) {
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (step < activeStep) {
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                  >
                    {step < activeStep ? '✓' : step}
                  </div>
                          <div style={{
                            fontSize: 'clamp(13px, 0.95vw, 15px)',
                            fontWeight: activeStep === step ? 600 : 400,
                            color: activeStep >= step ? 'var(--ul-primary)' : 'rgba(0, 0, 0, 0.4)',
                            transition: 'all 0.3s ease'
                          }}>
                    {step === 1 && 'Amount'}
                            {step === 2 && 'Payment'}
                            {step === 3 && 'Review'}
                          </div>
                </div>
              ))}
            </div>
          </div>

                {/* STEP 1: Amount & Frequency */}
                {activeStep === 1 && (
                    <div className="donate-step-container" style={{background: 'var(--white)', padding: 'clamp(50px, 5.26vw, 80px)', borderRadius: 'clamp(15px, 1.58vw, 25px)', boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)'}}>
                        <div style={{marginBottom: 'clamp(30px, 4.21vw, 60px)'}}>
                          <h2 className="ul-section-title" style={{marginBottom: 'clamp(12px, 1.26vw, 18px)', fontSize: 'clamp(22px, 2.1vw, 32px)', textAlign: 'left'}}>Make Your Donation</h2>
                          <p className="ul-section-descr" style={{fontSize: 'clamp(14px, 1.05vw, 17px)', lineHeight: 1.7, color: 'rgba(0, 0, 0, 0.7)', textAlign: 'left', margin: 0}}>Choose where your donation goes and select an amount that works for you.</p>
                        </div>

                        <div className="donate-category-grid" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(20px, 3.15vw, 50px)', marginBottom: 'clamp(30px, 5.26vw, 70px)'}}>
                          {/* Donation Category */}
                          <div>
                            <label style={{display: 'block', marginBottom: 'clamp(12px, 1.26vw, 16px)', fontWeight: 600, color: 'var(--ul-black)', fontSize: 'clamp(14px, 0.95vw, 16px)'}}>
                              Donation Category
                            </label>
                      <select
                        name="purpose"
                        value={formData.purpose}
                        onChange={handleInputChange}
                              style={{
                                width: '100%',
                                padding: 'clamp(16px, 1.68vw, 20px)',
                                border: '1px solid rgba(67, 67, 67, 0.15)',
                                borderRadius: 'clamp(8px, 0.63vw, 10px)',
                                fontSize: 'clamp(15px, 1.05vw, 17px)',
                                fontFamily: 'inherit',
                                color: 'var(--ul-black)',
                                backgroundColor: 'var(--white)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%2314B4B4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right clamp(16px, 1.68vw, 20px) center',
                                backgroundSize: '12px 8px',
                                paddingRight: 'clamp(45px, 3.68vw, 55px)'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = 'var(--ul-primary)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(20, 180, 180, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(67, 67, 67, 0.15)';
                                e.target.style.boxShadow = 'none';
                              }}
                      >
                        {donationPurposes.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>

                          {/* Frequency */}
                          <div>
                            <label style={{display: 'block', marginBottom: 'clamp(12px, 1.26vw, 16px)', fontWeight: 600, color: 'var(--ul-black)', fontSize: 'clamp(14px, 0.95vw, 16px)'}}>
                              Frequency
                            </label>
                            <select
                              name="frequency"
                              value={formData.frequency}
                              onChange={handleInputChange}
                              style={{
                                width: '100%',
                                padding: 'clamp(16px, 1.68vw, 20px)',
                                border: '1px solid rgba(67, 67, 67, 0.15)',
                                borderRadius: 'clamp(8px, 0.63vw, 10px)',
                                fontSize: 'clamp(15px, 1.05vw, 17px)',
                                fontFamily: 'inherit',
                                color: 'var(--ul-black)',
                                backgroundColor: 'var(--white)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%2314B4B4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right clamp(16px, 1.68vw, 20px) center',
                                backgroundSize: '12px 8px',
                                paddingRight: 'clamp(45px, 3.68vw, 55px)'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = 'var(--ul-primary)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(20, 180, 180, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(67, 67, 67, 0.15)';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              {donationFrequencies.map(freq => (
                                <option key={freq.value} value={freq.value}>{freq.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Donation Amount */}
                        <div>
                          <label style={{display: 'block', marginBottom: 'clamp(20px, 2.1vw, 28px)', fontWeight: 600, color: 'var(--ul-black)', fontSize: 'clamp(16px, 1.16vw, 18px)'}}>
                            Donation Amount
                          </label>
                          <div className="donate-amount-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'clamp(12px, 1.26vw, 16px)', marginBottom: 'clamp(20px, 2.1vw, 28px)'}}>
                        {presetAmounts.map(amt => (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => handlePresetAmount(amt)}
                            style={{
                                  padding: 'clamp(18px, 1.89vw, 24px)',
                                  border: formData.amount === amt.toString() ? '2px solid var(--ul-primary)' : '1px solid rgba(67, 67, 67, 0.15)',
                                  backgroundColor: formData.amount === amt.toString() ? 'var(--ul-primary)' : 'var(--white)',
                                  borderRadius: 'clamp(8px, 0.63vw, 10px)',
                              cursor: 'pointer',
                                  fontWeight: 600,
                                  color: formData.amount === amt.toString() ? 'var(--white)' : 'var(--ul-black)',
                                  fontSize: 'clamp(16px, 1.16vw, 18px)',
                                  transition: 'all 0.3s ease',
                                  boxShadow: formData.amount === amt.toString() ? '0 4px 12px rgba(20, 180, 180, 0.25)' : 'none'
                                }}
                                onMouseEnter={(e) => {
                                  if (formData.amount !== amt.toString()) {
                                    e.currentTarget.style.borderColor = 'var(--ul-primary)';
                                    e.currentTarget.style.backgroundColor = 'rgba(20, 180, 180, 0.05)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (formData.amount !== amt.toString()) {
                                    e.currentTarget.style.borderColor = 'rgba(67, 67, 67, 0.15)';
                                    e.currentTarget.style.backgroundColor = 'var(--white)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }
                            }}
                          >
                            ${amt}
                          </button>
                        ))}
                      </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.26vw, 16px)', padding: 'clamp(16px, 1.68vw, 20px)', background: 'var(--ul-gray3)', borderRadius: 'clamp(8px, 0.63vw, 10px)', border: `1px solid ${errors.amount ? '#dc3545' : 'transparent'}`}}>
                            <span style={{fontSize: 'clamp(20px, 1.68vw, 24px)', fontWeight: 600, color: 'var(--ul-black)'}}>$</span>
                        <input
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                              placeholder="Enter custom amount"
                          min="1"
                          step="0.01"
                              style={{
                                flex: 1,
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                fontSize: 'clamp(18px, 1.47vw, 20px)',
                                fontWeight: 500,
                                color: 'var(--ul-black)',
                                outline: 'none'
                              }}
                        />
                      </div>
                          {errors.amount && <small style={{display: 'block', color: '#dc3545', marginTop: 'clamp(10px, 1.05vw, 14px)', fontSize: 'clamp(13px, 0.95vw, 15px)'}}>{errors.amount}</small>}
                      </div>
                    </div>
                )}

                {/* STEP 2: Payment Information */}
                {activeStep === 2 && (
                  <div className="donate-step-container" style={{background: 'var(--white)', padding: 'clamp(50px, 5.26vw, 80px)', borderRadius: 'clamp(15px, 1.58vw, 25px)', boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)'}}>
                    <div className="text-center" style={{marginBottom: 'clamp(50px, 5.26vw, 70px)'}}>
                      <span className="ul-section-sub-title">Secure Payment</span>
                      <h2 className="ul-section-title" style={{marginBottom: 'clamp(15px, 1.58vw, 20px)'}}>Payment Information</h2>
                      <p className="ul-section-descr" style={{maxWidth: '600px', margin: '0 auto'}}>Stripe will securely collect your payment and contact information.</p>
                    </div>

                    {/* Donation Summary */}
                    <div style={{background: 'var(--ul-gray3)', padding: 'clamp(25px, 2.63vw, 35px)', borderRadius: 'clamp(10px, 0.84vw, 12px)', marginBottom: 'clamp(40px, 4.21vw, 60px)'}}>
                      <h4 style={{margin: '0 0 clamp(20px, 2.1vw, 28px) 0', fontSize: 'clamp(18px, 1.47vw, 20px)', fontWeight: 600, color: 'var(--ul-black)'}}>Donation Summary</h4>
                      <div className="donate-summary-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'clamp(15px, 1.58vw, 20px)'}}>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Amount to Donate</div>
                          <div style={{fontSize: 'clamp(18px, 1.47vw, 22px)', fontWeight: 600, color: 'var(--ul-black)'}}>${parseFloat(formData.amount).toFixed(2)}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Processing Fees</div>
                          <div style={{fontSize: 'clamp(18px, 1.47vw, 22px)', fontWeight: 600, color: 'rgba(0, 0, 0, 0.7)'}}>+${cardFees.fees.toFixed(2)}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Frequency</div>
                          <div style={{fontSize: 'clamp(16px, 1.26vw, 18px)', fontWeight: 600, color: 'var(--ul-black)', textTransform: 'capitalize'}}>{formData.frequency}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Total Charge</div>
                          <div style={{fontSize: 'clamp(20px, 1.68vw, 24px)', fontWeight: 700, color: 'var(--ul-primary)'}}>${cardFees.total.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Information Message */}
                    <div style={{background: 'rgba(20, 180, 180, 0.05)', padding: 'clamp(25px, 2.63vw, 35px)', borderRadius: 'clamp(10px, 0.84vw, 12px)', borderLeft: '4px solid var(--ul-primary)'}}>
                      <div className="ul-service-txt">
                        <h3 className="ul-service-title" style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(16px, 1.26vw, 18px)', display: 'flex', alignItems: 'center', gap: 'clamp(8px, 0.84vw, 12px)'}}>
                          <i className="flaticon-shield" style={{color: 'var(--ul-primary)', fontSize: 'clamp(20px, 1.68vw, 24px)'}}></i>
                          Secure Payment Processing
                        </h3>
                        <p className="ul-service-descr" style={{marginBottom: 0, lineHeight: 1.7}}>
                          When you proceed to the next step, you'll be redirected to Stripe's secure payment page where you can safely enter your payment information. Stripe will handle all payment processing, including collecting your card details and contact information. We never see or store your payment information.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Legal & Confirmation */}
                {activeStep === 3 && (
                  <div className="donate-step-container" style={{background: 'var(--white)', padding: 'clamp(50px, 5.26vw, 80px)', borderRadius: 'clamp(15px, 1.58vw, 25px)', boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)'}}>
                    <div className="text-center" style={{marginBottom: 'clamp(50px, 5.26vw, 70px)'}}>
                      <span className="ul-section-sub-title">Final Step</span>
                      <h2 className="ul-section-title" style={{marginBottom: 'clamp(15px, 1.58vw, 20px)'}}>Review & Confirm</h2>
                      <p className="ul-section-descr" style={{maxWidth: '600px', margin: '0 auto'}}>Review your donation details and confirm your payment.</p>
                    </div>

                    {/* Donation Summary */}
                    <div style={{background: 'var(--ul-gray3)', padding: 'clamp(25px, 2.63vw, 35px)', borderRadius: 'clamp(10px, 0.84vw, 12px)', marginBottom: 'clamp(40px, 4.21vw, 60px)'}}>
                      <h4 style={{margin: '0 0 clamp(20px, 2.1vw, 28px) 0', fontSize: 'clamp(18px, 1.47vw, 20px)', fontWeight: 600, color: 'var(--ul-black)'}}>Donation Summary</h4>
                      <div className="donate-summary-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'clamp(15px, 1.58vw, 20px)'}}>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Donation Amount</div>
                          <div style={{fontSize: 'clamp(18px, 1.47vw, 22px)', fontWeight: 600, color: 'var(--ul-black)'}}>${parseFloat(formData.amount).toFixed(2)}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Frequency</div>
                          <div style={{fontSize: 'clamp(16px, 1.26vw, 18px)', fontWeight: 600, color: 'var(--ul-black)', textTransform: 'capitalize'}}>{formData.frequency}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Processing Fees</div>
                          <div style={{fontSize: 'clamp(18px, 1.47vw, 22px)', fontWeight: 600, color: 'rgba(0, 0, 0, 0.7)'}}>+${cardFees.fees.toFixed(2)}</div>
                        </div>
                        <div>
                          <div style={{fontSize: 'clamp(13px, 0.95vw, 15px)', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 'clamp(5px, 0.53vw, 8px)'}}>Total</div>
                          <div style={{fontSize: 'clamp(20px, 1.68vw, 24px)', fontWeight: 700, color: 'var(--ul-primary)'}}>${cardFees.total.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Tax Information */}
                    <div style={{background: 'rgba(20, 180, 180, 0.05)', padding: 'clamp(25px, 2.63vw, 35px)', borderRadius: 'clamp(10px, 0.84vw, 12px)', borderLeft: '4px solid var(--ul-primary)', marginBottom: 'clamp(40px, 4.21vw, 60px)'}}>
                      <h3 style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(16px, 1.26vw, 18px)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 'clamp(8px, 0.84vw, 12px)'}}>
                        <i className="flaticon-document" style={{color: 'var(--ul-primary)', fontSize: 'clamp(20px, 1.68vw, 24px)'}}></i>
                        Tax Information
                      </h3>
                      <p style={{marginBottom: 0, lineHeight: 1.7, fontSize: 'clamp(14px, 0.95vw, 16px)', color: 'rgba(0,0,0,0.7)'}}>
                        Pathio is a registered 501(c)(3) nonprofit. Your donation is tax-deductible to the extent allowed by law. You will receive a tax receipt via email.
                      </p>
                    </div>

                    {/* Terms & Conditions */}
                    <div style={{marginBottom: 'clamp(25px, 2.63vw, 35px)'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', gap: 'clamp(12px, 1.26vw, 16px)', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="agreeToTerms"
                          checked={formData.agreeToTerms}
                          onChange={handleInputChange}
                          style={{marginTop: 'clamp(4px, 0.42vw, 6px)', width: 'clamp(18px, 1.26vw, 20px)', height: 'clamp(18px, 1.26vw, 20px)', cursor: 'pointer', accentColor: 'var(--ul-primary)'}}
                        />
                        <span style={{fontSize: 'clamp(14px, 0.95vw, 16px)', color: 'var(--ul-black)', lineHeight: 1.6}}>
                          <strong>Terms & Conditions:</strong> I agree to Pathio's terms of service and privacy policy. I understand that my donation is a non-refundable contribution to support our mission.
                          {errors.agreeToTerms && <small style={{display: 'block', color: '#dc3545', marginTop: 'clamp(8px, 0.84vw, 12px)'}}>{errors.agreeToTerms}</small>}
                        </span>
                      </label>
                    </div>

                    {/* Payment Processing Agreement */}
                    <div style={{marginBottom: 'clamp(25px, 2.63vw, 35px)'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', gap: 'clamp(12px, 1.26vw, 16px)', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="agreeToProcessing"
                          checked={formData.agreeToProcessing}
                          onChange={handleInputChange}
                          style={{marginTop: 'clamp(4px, 0.42vw, 6px)', width: 'clamp(18px, 1.26vw, 20px)', height: 'clamp(18px, 1.26vw, 20px)', cursor: 'pointer', accentColor: 'var(--ul-primary)'}}
                        />
                        <span style={{fontSize: 'clamp(14px, 0.95vw, 16px)', color: 'var(--ul-black)', lineHeight: 1.6}}>
                          <strong>Payment Authorization:</strong> I authorize this donation to be charged to my card in the amount of ${cardFees.total.toFixed(2)}. I understand that {formData.frequency === 'one-time' ? 'this is a one-time charge' : 'I will be charged ' + formData.frequency}.
                          {errors.agreeToProcessing && <small style={{display: 'block', color: '#dc3545', marginTop: 'clamp(8px, 0.84vw, 12px)'}}>{errors.agreeToProcessing}</small>}
                        </span>
                      </label>
                    </div>

                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="donate-nav-buttons" style={{display: 'flex', gap: 'clamp(12px, 1.26vw, 18px)', justifyContent: 'space-between', marginTop: 'clamp(30px, 4.21vw, 60px)'}}>
                  {activeStep > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={isProcessing}
                      style={{
                        padding: 'clamp(14px, 1.47vw, 18px) clamp(30px, 3.15vw, 45px)',
                        border: '1px solid rgba(67, 67, 67, 0.2)',
                        backgroundColor: 'var(--white)',
                        borderRadius: 'clamp(10px, 0.84vw, 12px)',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        fontSize: 'clamp(15px, 1.05vw, 17px)',
                        color: 'var(--ul-black)',
                        transition: 'all 0.3s ease',
                        opacity: isProcessing ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.borderColor = 'var(--ul-primary)';
                          e.currentTarget.style.color = 'var(--ul-primary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.borderColor = 'rgba(67, 67, 67, 0.2)';
                          e.currentTarget.style.color = 'var(--ul-black)';
                        }
                      }}
                    >
                      ← Back
                    </button>
                  )}

                  {activeStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={isProcessing}
                      style={{
                        padding: 'clamp(14px, 1.47vw, 18px) clamp(30px, 3.15vw, 45px)',
                        backgroundColor: 'var(--ul-primary)',
                        color: 'var(--white)',
                        border: 'none',
                        borderRadius: 'clamp(10px, 0.84vw, 12px)',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        marginLeft: 'auto',
                        fontSize: 'clamp(15px, 1.05vw, 17px)',
                        opacity: isProcessing ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(20, 180, 180, 0.25)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(20, 180, 180, 0.35)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(20, 180, 180, 0.25)';
                        }
                      }}
                    >
                      Next →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isProcessing}
                      style={{
                        padding: 'clamp(14px, 1.47vw, 18px) clamp(30px, 3.15vw, 45px)',
                        backgroundColor: isProcessing ? 'rgba(0, 0, 0, 0.2)' : 'var(--ul-primary)',
                        color: 'var(--white)',
                        border: 'none',
                        borderRadius: 'clamp(10px, 0.84vw, 12px)',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        marginLeft: 'auto',
                        fontSize: 'clamp(15px, 1.05vw, 17px)',
                        transition: 'all 0.3s ease',
                        boxShadow: isProcessing ? 'none' : '0 4px 15px rgba(20, 180, 180, 0.25)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(20, 180, 180, 0.35)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isProcessing) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(20, 180, 180, 0.25)';
                        }
                      }}
                    >
                      {isProcessing ? '⏳ Processing...' : 'Complete Donation'}
                    </button>
                  )}
                </div>
              </form>
                </div>
              </div>

              {/* Support Pathio Info - Right Column */}
              <div className="col-lg-4">
                <div style={{background: 'linear-gradient(135deg, var(--ul-primary) 0%, var(--ul-secondary) 100%)', padding: 'clamp(30px, 3.15vw, 50px)', borderRadius: 'clamp(15px, 1.05vw, 20px)', color: 'var(--white)', height: '100%'}}>
                  <h3 style={{color: 'var(--white)', marginBottom: 'clamp(20px, 2.1vw, 30px)', fontSize: 'clamp(22px, 1.68vw, 28px)'}}>Why Your Donation Matters</h3>
                  
                  <p style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: 'clamp(15px, 1.05vw, 17px)', lineHeight: 1.7, marginBottom: 'clamp(20px, 2.1vw, 30px)'}}>
                    Most students navigating healthcare pathways don't lack ambition—they lack clear guidance, trusted support, and community. Pathio exists to close that gap through peer mentorship, practical navigation, and community-rooted programming.
                  </p>

                  <p style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: 'clamp(15px, 1.05vw, 17px)', lineHeight: 1.7, marginBottom: 'clamp(15px, 1.58vw, 20px)', fontWeight: 600}}>
                    Your donation helps us:
                  </p>

                  <ul style={{paddingLeft: 'clamp(20px, 2.1vw, 28px)', marginBottom: 'clamp(25px, 2.63vw, 35px)', fontSize: 'clamp(15px, 1.05vw, 17px)', lineHeight: 1.8, color: 'rgba(255, 255, 255, 0.95)'}}>
                    <li style={{marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>Keep Pathio's programs running and accessible</li>
                    <li style={{marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>Build a stronger mentorship infrastructure for students</li>
                    <li style={{marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>Host events and panels that connect students with real experiences</li>
                    <li style={{marginBottom: 0}}>Maintain the tools and platforms that keep everything organized and sustainable</li>
                  </ul>

                  <div style={{paddingTop: 'clamp(25px, 2.63vw, 35px)', borderTop: '1px solid rgba(255, 255, 255, 0.2)'}}>
                    <p style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: 'clamp(15px, 1.05vw, 17px)', lineHeight: 1.7, marginBottom: 'clamp(12px, 1.26vw, 18px)', fontWeight: 600}}>
                      Every donation goes directly toward building and sustaining Pathio's mentorship programs, resources, and community efforts.
                    </p>
                    <p style={{color: 'rgba(255, 255, 255, 0.9)', fontSize: 'clamp(14px, 0.95vw, 16px)', lineHeight: 1.7, margin: 0}}>
                      We run on a lean budget, and your support helps us keep this work consistent, high-quality, and free to access.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="ul-section-spacing pt-0" style={{background: 'var(--ul-gray3)'}}>
          <div className="ul-container">
            <div className="ul-section-heading text-center" style={{marginBottom: 'clamp(40px, 4.21vw, 60px)'}}>
              <span className="ul-section-sub-title">Common Questions</span>
              <h2 className="ul-section-title">Frequently Asked Questions</h2>
              </div>
            
            {/* How Your Donation Will Be Used */}
            <div className="row" style={{marginBottom: 'clamp(30px, 3.15vw, 40px)'}}>
              <div className="col-12">
                <div className="ul-service" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 40px)', borderRadius: 'clamp(10px, 0.84vw, 15px)', boxShadow: '0 2px 15px rgba(0, 0, 0, 0.05)'}}>
                  <div className="ul-service-txt">
                    <h3 className="ul-service-title" style={{marginBottom: 'clamp(25px, 2.63vw, 35px)', fontSize: 'clamp(22px, 2.1vw, 28px)', color: 'var(--ul-black)', fontWeight: 700, paddingBottom: 'clamp(15px, 1.58vw, 20px)', borderBottom: '2px solid rgba(20, 180, 180, 0.15)'}}>
                      How Your Donation Will Be Used
                    </h3>
                    
                    <div style={{marginBottom: 'clamp(25px, 2.63vw, 35px)', paddingBottom: 'clamp(20px, 2.1vw, 30px)', borderBottom: '1px solid rgba(0, 0, 0, 0.08)'}}>
                      <h4 style={{fontSize: 'clamp(17px, 1.58vw, 21px)', fontWeight: 600, color: 'var(--ul-black)', marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>
                        <span style={{color: 'var(--ul-primary)'}}>General Fund</span> <span style={{color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9em'}}>(default)</span>
                      </h4>
                      <p className="ul-service-descr" style={{fontSize: 'clamp(15px, 1.16vw, 17px)', lineHeight: 1.7, marginBottom: 0, color: 'rgba(0, 0, 0, 0.75)'}}>
                        Flexible support that allows us to respond to what the organization needs most—whether that's program operations, outreach, or platform costs.
                      </p>
              </div>

                    <div style={{marginBottom: 'clamp(25px, 2.63vw, 35px)', paddingBottom: 'clamp(20px, 2.1vw, 30px)', borderBottom: '1px solid rgba(0, 0, 0, 0.08)'}}>
                      <h4 style={{fontSize: 'clamp(17px, 1.58vw, 21px)', fontWeight: 600, color: 'var(--ul-primary)', marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>
                        Programs & Outreach
                      </h4>
                      <p className="ul-service-descr" style={{fontSize: 'clamp(15px, 1.16vw, 17px)', lineHeight: 1.7, marginBottom: 'clamp(10px, 1.05vw, 14px)', color: 'rgba(0, 0, 0, 0.75)'}}>
                        Supports student-facing initiatives and community engagement, including:
                      </p>
                      <ul style={{paddingLeft: 'clamp(20px, 2.1vw, 28px)', marginBottom: 0, fontSize: 'clamp(15px, 1.16vw, 17px)', lineHeight: 1.8, color: 'rgba(0, 0, 0, 0.7)'}}>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Mentorship program coordination and materials
                        </li>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Panels and workshops with students and healthcare professionals
                        </li>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Tabling and in-person outreach at campus and community events
                        </li>
                        <li style={{marginBottom: 0, position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Printed materials and basic event supplies
                        </li>
                      </ul>
              </div>

                    <div>
                      <h4 style={{fontSize: 'clamp(17px, 1.58vw, 21px)', fontWeight: 600, color: 'var(--ul-primary)', marginBottom: 'clamp(10px, 1.05vw, 14px)'}}>
                        Operations & Platform
                      </h4>
                      <p className="ul-service-descr" style={{fontSize: 'clamp(15px, 1.16vw, 17px)', lineHeight: 1.7, marginBottom: 'clamp(10px, 1.05vw, 14px)', color: 'rgba(0, 0, 0, 0.75)'}}>
                        Supports the behind-the-scenes infrastructure that keeps Pathio running:
                      </p>
                      <ul style={{paddingLeft: 'clamp(20px, 2.1vw, 28px)', marginBottom: 0, fontSize: 'clamp(15px, 1.16vw, 17px)', lineHeight: 1.8, color: 'rgba(0, 0, 0, 0.7)'}}>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Website hosting, domain, and platform maintenance
                        </li>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Forms, scheduling tools, and communication software
                        </li>
                        <li style={{marginBottom: 'clamp(8px, 0.84vw, 12px)', position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Design and content tools used to produce outreach materials
                        </li>
                        <li style={{marginBottom: 0, position: 'relative', paddingLeft: 'clamp(8px, 0.84vw, 12px)'}}>
                          <span style={{position: 'absolute', left: 0, color: 'var(--ul-primary)'}}>•</span>
                          Basic admin costs that allow us to operate responsibly as a nonprofit
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row row-cols-md-2 row-cols-1 gy-4">
              <div className="col">
                <div className="ul-service" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 40px)', borderRadius: 'clamp(10px, 0.84vw, 15px)', height: '100%'}}>
                  <div className="ul-service-txt">
                    <h3 className="ul-service-title" style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(18px, 1.47vw, 22px)'}}>Is my donation secure?</h3>
                    <p className="ul-service-descr" style={{marginBottom: 0}}>Yes! We use industry-leading payment processors (Stripe) with encryption and PCI compliance. Your payment information is never stored on our servers.</p>
              </div>
              </div>
              </div>
              <div className="col">
                <div className="ul-service" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 40px)', borderRadius: 'clamp(10px, 0.84vw, 15px)', height: '100%'}}>
                  <div className="ul-service-txt">
                    <h3 className="ul-service-title" style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(18px, 1.47vw, 22px)'}}>Is my donation tax-deductible?</h3>
                    <p className="ul-service-descr" style={{marginBottom: 0}}>Yes! Pathio is a registered 501(c)(3) nonprofit organization. You'll receive a tax receipt via email for your records.</p>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="ul-service" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 40px)', borderRadius: 'clamp(10px, 0.84vw, 15px)', height: '100%'}}>
                  <div className="ul-service-txt">
                    <h3 className="ul-service-title" style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(18px, 1.47vw, 22px)'}}>Can I cancel my recurring donation?</h3>
                    <p className="ul-service-descr" style={{marginBottom: 0}}>Yes! You can manage or cancel your recurring donations anytime. Contact us at pathio.org@gmail.com or update your preferences in your donor dashboard.</p>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="ul-service" style={{background: 'var(--white)', padding: 'clamp(30px, 3.15vw, 40px)', borderRadius: 'clamp(10px, 0.84vw, 15px)', height: '100%'}}>
                  <div className="ul-service-txt">
                    <h3 className="ul-service-title" style={{marginBottom: 'clamp(12px, 1.26vw, 16px)', fontSize: 'clamp(18px, 1.47vw, 22px)'}}>What if my payment fails?</h3>
                    <p className="ul-service-descr" style={{marginBottom: 0}}>We'll notify you immediately via email. You can retry with a different payment method or contact our support team for assistance.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Donate;
